import React from 'react';
import { FrameAnimation, animate } from 'shared/utils';

type FadeProps = {
  show: boolean,
  children: React.ReactElement,
};

const Fade = (props: FadeProps) => {
  const {
    show,
    children,
  } = props;

  const mountedRef = React.useRef(false);
  const showRef = React.useRef<boolean>();
  const elementRef = React.useRef<HTMLElement>();
  const renderElementRef = React.useRef<boolean>();
  const animateElementRef = React.useRef<Function>();
  const currentAnimationRef = React.useRef<FrameAnimation>();
  const [renderElement, setRenderElement] = React.useState(show);
  showRef.current = show;
  renderElementRef.current = renderElement;

  animateElementRef.current = () => {
    const startAnimation = animate(250, (duration, progress) => {
      if (showRef.current) {
        elementRef.current.style.opacity = ((1 / duration) * progress).toString();
      } else {
        elementRef.current.style.opacity = (1 - (1 / duration) * progress).toString();
      }
    });

    currentAnimationRef.current = startAnimation();

    return currentAnimationRef.current.animationPromise;
  };

  React.useLayoutEffect(() => {
    if (mountedRef.current) {
      currentAnimationRef.current?.stopAnimation();

      if (show) {
        if (!renderElementRef.current) {
          setRenderElement(true);
        }
      } else {
        animateElementRef.current().then((result) => {
          if (result.status === 'finished') {
            setRenderElement(false);
          }
        });
      }
    }
  }, [show]);

  React.useLayoutEffect(() => {
    if (mountedRef.current && renderElement) {
      animateElementRef.current();
    }
  }, [renderElement]);

  React.useEffect(() => {
    mountedRef.current = true;
  }, []);

  return renderElement && React.cloneElement(children, {
    ref: elementRef,
  });
};

export default Fade;
